import { format, parseISO, subMonths } from "date-fns";
import { TrendingDown, TrendingUp } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { USDollar } from "~/functions";
import api from "~/services/api";
import useReportsStore from "~/store/reportsStore";

function ChartGrossValues({ onFilialBreakdownChange, dreConfigId = null, dateType = null }) {
  const filial = useSelector((state) => state.auth.filial);
  const { filters } = useReportsStore();
  const period = filters.periodsByReport?.GrossValues || filters.period;
  const [gross, setGross] = useState({
    total: 0,
    amount: 0,
    discount: 0,
    fees: 0,
    discountPercent: 0,
  });
  const [showFilialBreakdown, setShowFilialBreakdown] = useState(false);
  const [filialData, setFilialData] = useState([]);
  function updateData() {
    const from_date = format(period.from, "yyyy-MM-dd");
    const until_date = format(period.to, "yyyy-MM-dd");
    const useCompare =
      period?.label === "Month vs Month" || period?.label === "Year vs Year";
    const from_date_before =
      useCompare && period?.compare_from
        ? format(period.compare_from, "yyyy-MM-dd")
        : null;
    const until_date_before =
      useCompare && period?.compare_to
        ? format(period.compare_to, "yyyy-MM-dd")
        : null;
    const compareQuery =
      useCompare && from_date_before && until_date_before
        ? `&from_date_before=${from_date_before}&until_date_before=${until_date_before}`
        : "";

    const dreQuery = dreConfigId ? `&dre_id=${dreConfigId}` : "";
    const dateTypeQuery = dateType ? `&date_type=${dateType}` : "";

    api
      .get(
        `/receivables-dashboard/invoices?from_date=${from_date}&until_date=${until_date}${compareQuery}${dreQuery}${dateTypeQuery}`
      )
      .then((response) => {
        setGross(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function fetchFilialBreakdown() {

    const from_date = format(period.from, "yyyy-MM-dd");
    const until_date = format(period.to, "yyyy-MM-dd");
    const useCompare =
      period?.label === "Month vs Month" || period?.label === "Year vs Year";
    const from_date_before =
      useCompare && period?.compare_from
        ? format(period.compare_from, "yyyy-MM-dd")
        : null;
    const until_date_before =
      useCompare && period?.compare_to
        ? format(period.compare_to, "yyyy-MM-dd")
        : null;
    const compareQuery =
      useCompare && from_date_before && until_date_before
        ? `&from_date_before=${from_date_before}&until_date_before=${until_date_before}`
        : "";

    const dreQuery = dreConfigId ? `&dre_id=${dreConfigId}` : "";
    const dateTypeQuery = dateType ? `&date_type=${dateType}` : "";

    api
      .get(
        `/receivables-dashboard/invoices-by-filial?from_date=${from_date}&until_date=${until_date}${compareQuery}${dreQuery}${dateTypeQuery}`
      )
      .then((response) => {
        const filials = response.data?.filials || [];
        setFilialData(filials);
        setShowFilialBreakdown(true);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function handleBarClick() {
    if (!showFilialBreakdown) {
      fetchFilialBreakdown();
    } else {
      setShowFilialBreakdown(false);
    }
  }

  useEffect(() => {
    updateData();
    if (showFilialBreakdown) {
      fetchFilialBreakdown();
    }
  }, [period, showFilialBreakdown, dreConfigId, dateType]);

  useEffect(() => {
    updateData();
    setShowFilialBreakdown(false);
  }, [filial, dreConfigId, dateType]);

  useEffect(() => {
    if (onFilialBreakdownChange) {
      onFilialBreakdownChange({ show: showFilialBreakdown, data: filialData });
    }
  }, [showFilialBreakdown, filialData]);

  return (
    <div className="w-full p-2 px-4 border rounded-lg mb-4">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row items-start justify-between w-full">
          <div className="flex flex-col items-start justify-start mb-2">
            <h1 className="text-lg font-bold">Invoices</h1>
            <div className="text-sm font-extralight text-gray-500">
              Total Gross of{" "}
              {USDollar.format((gross.amount + gross.discount).toFixed(0))}
            </div>
          </div>
          {/* <h1 className="text-sm font-extralight text-gray-500 flex flex-col items-center gap-1 px-1 rounded-sm hover:bg-zinc-50 border-transparent hover:border-zinc-200 border group relative">
            <div className="flex flex-row items-center gap-1">
              <ChevronDown size={12} /> This month
            </div>
            <div className="hidden absolute right-0 top-5 w-full flex flex-col items-start justify-center gap-1 bg-zinc-50 border-t group-hover:block">
              <span>Last Month</span>
            </div>
          </h1> */}
        </div>
      </div>
      <div className="w-full flex flex-col items-start justify-center text-xs gap-2">
        <div className="w-full flex flex-row items-center justify-between">
          <div className="flex flex-row items-center justify-start gap-2">
            <div className="w-3 h-3 bg-mila_orange rounded-sm"></div>
            <div className="text-zinc-600 flex flex-col items-start">
              <strong className="text-sm text-black flex flex-row items-center gap-2">
                {USDollar.format(gross.amount.toFixed(0))}
                {gross.differencePeriodBefore >= 0 ? (
                  <div
                    className="flex flex-row items-center justify-start gap-1"
                    text-xs
                  >
                    {gross.differencePeriodBefore}%
                    <TrendingUp size={16} className="text-mila_orange" />
                  </div>
                ) : (
                  <div className="flex flex-row items-center justify-start text-primary text-xs">
                    {gross.differencePeriodBefore}%
                    <TrendingDown size={16} />
                  </div>
                )}
              </strong>{" "}
              Amount
            </div>
          </div>
          <div className="flex flex-row items-center justify-start gap-2">
            <div className="text-zinc-600 flex flex-col items-end">
              <strong className="text-sm text-black">
                {USDollar.format(gross.discount.toFixed(0))} (
                {gross.discountPercent}%)
              </strong>{" "}
              (-) Discount
            </div>
            <div className="w-3 h-3 bg-mila_orange/50 rounded-sm"></div>
          </div>
        </div>
        <div
          className="w-full h-6 rounded-md overflow-hidden flex flex-row items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleBarClick}
        >
          <div className="h-8 flex-1 bg-mila_orange"></div>
          <div
            className={`h-8 bg-mila_orange/50`}
            style={{
              width: ((gross.discount / gross.amount) * 100).toFixed(0) + "%",
            }}
          ></div>
        </div>
      </div>
      <div className="w-full flex flex-col items-start justify-center text-xs gap-2 mt-2">
        <div className="w-full flex flex-row items-center justify-between">
          <div className="flex flex-row items-center justify-start gap-2">
            <div className="w-3 h-3 bg-primary/50 rounded-sm"></div>
            <div className="text-zinc-600 flex flex-col items-start">
              <strong className="text-sm text-black">
                {USDollar.format(gross.fees.toFixed(0))}
              </strong>{" "}
              Fees
            </div>
          </div>
        </div>
        <div className="w-full h-6 rounded-md overflow-hidden flex flex-row items-center justify-start bg-primary/5">
          <div
            className={`h-8 bg-primary/50`}
            style={{
              width:
                ((gross.fees / (gross.amount - gross.discount)) * 100).toFixed(
                  0
                ) + "%",
              minWidth: "10%",
            }}
          ></div>
        </div>
      </div>
    </div>
  );
}

export default ChartGrossValues;
